import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

import { COLORS } from '@theme/colors';
import { media } from '@theme/media';
import { blurredTransparentHeaderBackgroundStyles, linkStyles } from '@components/header/header.styles';
import { NARROW_CONTAINER_WIDTH } from '@components/blocks';
import { FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, typography } from '@theme/font';
import { OFFSET } from '@theme/helpers';

export const MotionContainer = styled(motion.div)`
  position: absolute;
  top: ${OFFSET.M};
  width: 100%;
  ${blurredTransparentHeaderBackgroundStyles};
  z-index: -1;
  box-shadow: 0 2px 80px 1px rgb(0 0 0 / 20%);
`;

export const GroupingIntro = styled.div`
  max-width: 576px;
  text-align: center;
  margin: 0 auto ${OFFSET.M};
  padding: 0 ${OFFSET.S};

  ${media.mobile} {
    padding: 0;
  }
`;

export const GroupingTitle = styled.h3`
  ${typography({ size: FONT_SIZE.M, weight: FONT_WEIGHT.REGULAR })};
  text-transform: uppercase;
  margin: 0;
`;

export const GroupingDescription = styled.div`
  p {
    ${typography({ size: FONT_SIZE.M, weight: FONT_WEIGHT.REGULAR })};
    margin: 0;
  }
`;

export const ControlsContainer = styled.div<{ isFilterSelected: boolean }>`
  background: ${COLORS.WHITE};
  position: relative;
  display: flex;
  justify-content: space-between;
  padding-right: 13px;
  margin-left: auto;

  > button {
    margin-left: 8px;
    ${({ isFilterSelected }) =>
      isFilterSelected &&
      css`
        min-width: 60px;
      `}
  }

  ${media.tablet} {
    margin-left: auto;
  }

  &::before {
    content: '';
    width: 50px;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), white);
    display: block;
    position: absolute;
    left: -50px;
  }
`;

export const DesktopGroupingsContainer = styled.div<{ isArtistDesignedView?: boolean }>`
  ${({ isArtistDesignedView }) =>
    isArtistDesignedView
      ? css`
          width: 100%;
        `
      : css`
          width: calc(${NARROW_CONTAINER_WIDTH}px - 50px);
        `};
  overflow: hidden;
  display: none;

  ${media.mobile} {
    display: block;
  }
`;

export const GroupingsList = styled.ul<{ isHidden: boolean }>`
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  height: 80px;
  margin-right: 50px;

  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hide;
  -webkit-overflow-scrolling: touch;

  ${({ isHidden }) =>
    isHidden &&
    css`
      display: none;
    `}

  &::after {
    content: '';
    width: 50px;
    height: 20px;
    display: block;
    position: relative;
    flex-shrink: 0;
  }
`;

export const Grouping = styled.a<{ isActive: boolean }>`
  white-space: nowrap;
  ${linkStyles};
  padding: 5px 13px;
  margin: 0;
  color: ${COLORS.DARK_GREY};
  ${({ isActive }) =>
    isActive &&
    css`
      background: ${COLORS.WHITE};
      color: ${COLORS.BLACK};
    `};

  ${media.tablet} {
    padding: 7px 13px;
  }
`;

export const AllArtworksGrouping = styled(Grouping)`
  display: inline-block;
  height: ${OFFSET.M};
`;

export const TradeProgram = styled(Grouping)`
  display: inline-block;
  height: ${OFFSET.M};
`;

export const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  height: ${OFFSET.M};
  border-top: 1px solid ${COLORS.BLACK10};
  padding: 0;
`;

const menuButtonStyles = css`
  ${typography({ spacing: LETTER_SPACING.M })};
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const menuButtonWithHoverStyles = css`
  ${menuButtonStyles};

  &:hover {
    color: ${COLORS.GUARDSMAN_RED};
  }
`;

export const MenuOption = styled.button`
  ${menuButtonWithHoverStyles};
`;

export const TradeModal = styled.dialog.attrs({ open: true })`
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.1);
  position: fixed;
  backdrop-filter: blur(24px);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: ${LETTER_SPACING.NORMAL};
  border: 2px solid transparent;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 576px;
  width: 100%;
  margin: ${OFFSET.L} 0;
`;

export const Title = styled.h3`
  ${typography({ size: FONT_SIZE.M, weight: FONT_WEIGHT.REGULAR })};
  margin: 0 0 ${OFFSET.M};
  text-align: center;
`;

export const Controls = styled.div`
  display: flex;
  gap: ${OFFSET.XS};
`;

export const Button = styled.button`
  ${typography({ weight: FONT_WEIGHT.REGULAR })};
  width: 50%;
  border: 1px solid ${COLORS.BLACK};
  text-transform: uppercase;
  height: ${OFFSET.M};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MobileGroupingsButton = styled.button`
  ${media.mobile} {
    display: none;
  }
  position: relative;
  padding: 0 13px;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${COLORS.BLACK};
    position: absolute;
    top: 50%;
    right: -6px;
    transform: translateY(-50%);
  }
`;
